<template>
    <VDialog v-model="display" :width="dialogWidth" content-class="v-application v-application--is-ltr theme--light">
        <template #activator="{ on }">
            <VTextField
                ref="input"
                v-bind="textFieldProps"
                :class="classList"
                :disabled="disabled"
                :loading="loading"
                :label="label"
                :value="formattedDatetime"
                :error="!!errorMessage"
                :error-messages="errorMessage"
                :rules="validationRules"
                readonly
                outlined
                v-on="on"
            >
                <template #label>
                    <SrInputLabel :label="label" :required="required" />
                </template>
                <template #progress>
                    <slot name="progress">
                        <VProgressLinear color="primary" indeterminate absolute height="2" />
                    </slot>
                </template>
            </VTextField>
        </template>

        <VCard>
            <VCardText class="px-0 py-0">
                <SrTabs v-model="activeTab" :slider-color="tabsSliderColor" :background-color="tabsBackgroundColor" fixed-tabs>
                    <VTab key="calendar">
                        <slot name="dateIcon">
                            <VIcon>mdi-calendar-range</VIcon>
                        </slot>
                    </VTab>
                    <VTab key="timer" :disabled="dateSelected">
                        <slot name="timeIcon">
                            <VIcon>mdi-clock-outline</VIcon>
                        </slot>
                    </VTab>
                    <VTabItem key="calendar">
                        <VDatePicker
                            v-model="date"
                            class="sr-date-time-picker__picker"
                            v-bind="datePickerProps"
                            :color="color"
                            :min="minDate"
                            :max="maxDate"
                            full-width
                            @input="showTimePicker"
                        />
                    </VTabItem>
                    <VTabItem key="timer">
                        <VTimePicker
                            ref="timer"
                            v-model="time"
                            class="sr-date-time-picker__picker"
                            v-bind="timePickerProps"
                            :min="minTime"
                            :max="maxTime"
                            :color="color"
                            full-width
                        />
                    </VTabItem>
                </SrTabs>
            </VCardText>
            <VCardActions>
                <VSpacer />
                <slot name="actions" :date-time-picker="this">
                    <SrButton @click.native="clearHandler">
                        {{ clearText }}
                    </SrButton>
                    <SrButton type="secondary" @click="okHandler">
                        {{ okText }}
                    </SrButton>
                </slot>
            </VCardActions>
        </VCard>
    </VDialog>
</template>

<script>
import { format, isSameDay } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { USER_INPUT_CLASS } from '../../utils/classNames';
import { SrInputLabel } from '../SrInputLabel';
import { SrButton } from '../SrButton';
import { SrTabs } from '../SrTabs';
import {
    DEFAULT_CLEAR_TEXT,
    DEFAULT_DATE,
    DEFAULT_DATE_FORMAT,
    DEFAULT_DIALOG_WIDTH,
    DEFAULT_DISPLAY_TIME_FORMAT,
    DEFAULT_OK_TEXT,
    DEFAULT_TIME,
    DEFAULT_TIME_FORMAT,
} from './config';

export default {
    name: 'SrDateTimePicker',
    components: {
        SrButton,
        SrInputLabel,
        SrTabs,
    },
    model: {
        prop: 'datetime',
        event: 'input',
    },
    props: {
        datetime: {
            type: [Date, String],
            default: null,
        },
        disabled: {
            type: Boolean,
        },
        loading: {
            type: Boolean,
        },
        label: {
            type: String,
            default: '',
        },
        dialogWidth: {
            type: Number,
            default: DEFAULT_DIALOG_WIDTH,
        },
        dateFormat: {
            type: String,
            default: DEFAULT_DATE_FORMAT,
        },
        timeFormat: {
            type: String,
            default: DEFAULT_DISPLAY_TIME_FORMAT,
        },
        clearText: {
            type: String,
            default: DEFAULT_CLEAR_TEXT,
        },
        okText: {
            type: String,
            default: DEFAULT_OK_TEXT,
        },
        textFieldProps: {
            type: Object,
            default: null,
        },
        datePickerProps: {
            type: Object,
            default: null,
        },
        timePickerProps: {
            type: Object,
            default: null,
        },
        color: {
            type: String,
            default: 'primary',
        },
        okColor: {
            type: String,
            default: 'primary darken-1',
        },
        clearColor: {
            type: String,
            default: 'grey lighten-1',
        },
        tabsBackgroundColor: {
            type: String,
            default: '',
        },
        tabsSliderColor: {
            type: String,
            default: '',
        },
        errorMessage: {
            type: String,
            default: '',
        },
        min: {
            type: Date,
            default: null,
        },
        max: {
            type: Date,
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        rules: {
            type: Array,
            default: () => [],
        },
        targetTimeZone: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            display: false,
            activeTab: 0,
            date: DEFAULT_DATE,
            time: DEFAULT_TIME,
        };
    },
    computed: {
        dateTimeFormat() {
            return `${this.dateFormat} ${this.timeFormat}`;
        },
        formattedDatetime() {
            if (!this.selectedDatetime) {
                return '';
            }

            const datetime = utcToZonedTime(this.selectedDatetime, this.targetTimeZone);

            return format(datetime, this.dateTimeFormat);
        },
        selectedDatetime() {
            if (this.date && this.time) {
                return zonedTimeToUtc(`${this.date}T${this.time}`, this.targetTimeZone);
            }

            return null;
        },
        selectedDatetimeString() {
            return this.selectedDatetime?.toISOString();
        },
        minDate() {
            return this.min ? format(this.min, DEFAULT_DATE_FORMAT) : null;
        },
        minTime() {
            const { selectedDatetime, min } = this;

            if (selectedDatetime === null || min === null || !isSameDay(selectedDatetime, min)) {
                return null;
            }

            return format(min, DEFAULT_TIME_FORMAT);
        },
        maxDate() {
            return this.max ? format(this.max, DEFAULT_DATE_FORMAT) : null;
        },
        maxTime() {
            const { selectedDatetime, max } = this;

            if (selectedDatetime === null || max === null || !isSameDay(selectedDatetime, max)) {
                return null;
            }

            return format(max, DEFAULT_TIME_FORMAT);
        },

        dateSelected() {
            return !this.date;
        },
        validationRules() {
            const rules = [...this.rules];

            if (this.required) {
                rules.unshift((value) => {
                    const isFilled = typeof value === 'string' && Boolean(value.trim());
                    return isFilled || "Date can't be empty";
                });
            }

            return rules;
        },
        classList() {
            return [USER_INPUT_CLASS];
        },
    },
    watch: {
        datetime() {
            this.init();
        },
        targetTimeZone() {
            this.$emit('input', this.selectedDatetimeString);
        },
    },
    created() {
        this.init();
    },
    methods: {
        init() {
            if (!this.datetime) {
                this.clearHandler();
            } else {
                let initDateTime;
                if (this.datetime instanceof Date) {
                    initDateTime = this.datetime;
                } else if (typeof this.datetime === 'string' || this.datetime instanceof String) {
                    initDateTime = utcToZonedTime(this.datetime, this.targetTimeZone);
                }

                this.date = format(initDateTime, DEFAULT_DATE_FORMAT);
                this.time = format(initDateTime, DEFAULT_TIME_FORMAT);
            }
        },
        okHandler() {
            this.resetPicker();
            this.$emit('input', this.selectedDatetimeString);
        },
        clearHandler() {
            this.resetPicker();
            this.date = DEFAULT_DATE;
            this.time = DEFAULT_TIME;
            this.$emit('input', null);
        },
        resetPicker() {
            this.display = false;
            this.activeTab = 0;
            if (this.$refs.timer) {
                this.$refs.timer.selectingHour = true;
            }
        },
        showTimePicker() {
            this.activeTab = 1;
        },
    },
};
</script>
